import {
  metricKeys,
  recognitionMetricKeys,
  type CaptureProfile,
  type MetricKey,
  type MetricRecognitionIntervalMs,
  type RecognitionMetricKey,
} from '@/domain/types'

export const MIN_CAPTURE_FPS = 1
export const DEFAULT_CAPTURE_FPS = 2
export const MAX_CAPTURE_FPS = 8
export const METRIC_CAPTURE_FPS_VERSION = 5
export const METRIC_RECOGNITION_INTERVAL_VERSION = 2

export const metricRecognitionIntervalOptions = [
  10_000,
  5_000,
  4_000,
  3_000,
  2_000,
  1_000,
  500,
  250,
] as const satisfies readonly Exclude<MetricRecognitionIntervalMs, 0>[]
export const jobRecognitionIntervalOptions = [
  30_000,
  ...metricRecognitionIntervalOptions,
] as const satisfies readonly Exclude<MetricRecognitionIntervalMs, 0>[]

const nonDisableableRecognitionMetrics = new Set<RecognitionMetricKey>(['level', 'exp', 'jobName'])
const DEFAULT_METRIC_RECOGNITION_INTERVALS_MS: Readonly<Record<RecognitionMetricKey, MetricRecognitionIntervalMs>> = {
  level: 4_000,
  exp: 1_000,
  hp: 2_000,
  mp: 2_000,
  mesos: 1_000,
  mapName: 4_000,
  jobName: 30_000,
}
const LEGACY_OCR_BASE_INTERVAL_MS: Readonly<Record<MetricKey, number>> = {
  level: 5_000,
  exp: 1_000,
  hp: 1_000,
  mp: 1_000,
  mesos: 1_000,
  mapName: 3_000,
}

const RECOGNITION_INTERVAL_REFERENCE_FPS = 4
const PREVIOUS_DEFAULT_CAPTURE_FPS = 4
const PREVIOUS_EXP_CAPTURE_FPS = 8
const PREVIOUS_MESOS_CAPTURE_FPS = 8

export type MetricRecognitionIntervalOverrides = Partial<Record<RecognitionMetricKey, MetricRecognitionIntervalMs>>

export function normalizeCaptureFps(value: number | undefined): number {
  if (!Number.isFinite(value)) return DEFAULT_CAPTURE_FPS
  return Math.min(MAX_CAPTURE_FPS, Math.max(MIN_CAPTURE_FPS, Math.round(value!)))
}

export function recommendedMetricRecognitionIntervalsMs(): Record<RecognitionMetricKey, MetricRecognitionIntervalMs> {
  return { ...DEFAULT_METRIC_RECOGNITION_INTERVALS_MS }
}

export function metricCanDisableRecognition(metric: RecognitionMetricKey): boolean {
  return !nonDisableableRecognitionMetrics.has(metric)
}

export function recognitionIntervalOptionsForMetric(
  metric: RecognitionMetricKey,
): readonly Exclude<MetricRecognitionIntervalMs, 0>[] {
  return metric === 'jobName' ? jobRecognitionIntervalOptions : metricRecognitionIntervalOptions
}

export function normalizeMetricRecognitionIntervalMs(
  metric: RecognitionMetricKey,
  value: number | undefined,
): MetricRecognitionIntervalMs {
  if (value === 0) {
    return metricCanDisableRecognition(metric) ? 0 : DEFAULT_METRIC_RECOGNITION_INTERVALS_MS[metric]
  }
  return recognitionIntervalOptionsForMetric(metric).includes(value as Exclude<MetricRecognitionIntervalMs, 0>)
    ? value as MetricRecognitionIntervalMs
    : DEFAULT_METRIC_RECOGNITION_INTERVALS_MS[metric]
}

export function normalizeMetricRecognitionIntervalsMs(
  values: Readonly<Partial<Record<RecognitionMetricKey, number>>> | undefined,
): Record<RecognitionMetricKey, MetricRecognitionIntervalMs> {
  const normalized = Object.fromEntries(recognitionMetricKeys.map((metric) => [
    metric,
    normalizeMetricRecognitionIntervalMs(metric, values?.[metric]),
  ])) as Record<RecognitionMetricKey, MetricRecognitionIntervalMs>
  const resourceInterval = fasterResourceInterval(normalized.hp, normalized.mp)
  normalized.hp = resourceInterval
  normalized.mp = resourceInterval
  return normalized
}

export function migrateRecognitionFrequencies(
  recognition: CaptureProfile['recognition'],
): CaptureProfile['recognition'] {
  let captureFps = normalizeCaptureFps(recognition.captureFps)
  const intervalVersion = recognition.metricRecognitionIntervalsVersion ?? 0
  const metricRecognitionIntervalsMs = intervalVersion >= 1
    ? normalizeMetricRecognitionIntervalsMs(recognition.metricRecognitionIntervalsMs)
    : migrateLegacyMetricIntervals(recognition)

  if (intervalVersion >= METRIC_RECOGNITION_INTERVAL_VERSION) {
    return {
      captureFps,
      metricRecognitionIntervalsMs,
      metricRecognitionIntervalsVersion: METRIC_RECOGNITION_INTERVAL_VERSION,
      minConfidence: recognition.minConfidence,
      ocrChangeThreshold: recognition.ocrChangeThreshold,
    }
  }

  if (captureFps === PREVIOUS_DEFAULT_CAPTURE_FPS) {
    captureFps = DEFAULT_CAPTURE_FPS
  }
  return {
    captureFps,
    metricRecognitionIntervalsMs,
    metricRecognitionIntervalsVersion: METRIC_RECOGNITION_INTERVAL_VERSION,
    minConfidence: recognition.minConfidence,
    ocrChangeThreshold: recognition.ocrChangeThreshold,
  }
}

function migrateLegacyMetricIntervals(
  recognition: CaptureProfile['recognition'],
): Record<RecognitionMetricKey, MetricRecognitionIntervalMs> {
  const defaults = recommendedMetricRecognitionIntervalsMs()
  const version = recognition.metricCaptureFpsVersion ?? 0

  const migrated = { ...defaults }
  for (const metric of metricKeys) {
    const raw = recognition.metricCaptureFps?.[metric] ?? recognition.captureFps
    if (!Number.isFinite(raw)) continue
    const fps = normalizeCaptureFps(raw)
    if (fps === legacyMetricDefault(version, metric)) continue
    const effectiveInterval = Math.round(LEGACY_OCR_BASE_INTERVAL_MS[metric] * RECOGNITION_INTERVAL_REFERENCE_FPS / fps)
    migrated[metric] = closestRecognitionInterval(effectiveInterval)
  }
  return normalizeMetricRecognitionIntervalsMs(migrated)
}

function fasterResourceInterval(
  hp: MetricRecognitionIntervalMs,
  mp: MetricRecognitionIntervalMs,
): MetricRecognitionIntervalMs {
  if (hp === 0 && mp === 0) return 0
  if (hp === 0) return mp
  if (mp === 0) return hp
  return Math.min(hp, mp) as MetricRecognitionIntervalMs
}

function legacyMetricDefault(version: number, metric: MetricKey): number {
  if (version >= METRIC_CAPTURE_FPS_VERSION) return DEFAULT_CAPTURE_FPS
  return previousMetricDefault(version, metric)
}

function closestRecognitionInterval(value: number): MetricRecognitionIntervalMs {
  return metricRecognitionIntervalOptions.reduce<Exclude<MetricRecognitionIntervalMs, 0>>((closest, candidate) => {
    const distance = Math.abs(candidate - value)
    const closestDistance = Math.abs(closest - value)
    return distance < closestDistance || (distance === closestDistance && candidate > closest)
      ? candidate
      : closest
  }, metricRecognitionIntervalOptions[0])
}

function previousMetricDefault(version: number, metric: MetricKey): number {
  if (metric === 'exp' && version >= 3) return PREVIOUS_EXP_CAPTURE_FPS
  if (metric === 'mesos' && version >= 4) return PREVIOUS_MESOS_CAPTURE_FPS
  return PREVIOUS_DEFAULT_CAPTURE_FPS
}

export function effectiveMetricRecognitionIntervalMs(
  recognition: CaptureProfile['recognition'],
  metric: RecognitionMetricKey,
): MetricRecognitionIntervalMs {
  return normalizeMetricRecognitionIntervalMs(
    metric,
    recognition.metricRecognitionIntervalsMs?.[metric],
  )
}

export function metricRecognitionEnabled(
  recognition: CaptureProfile['recognition'],
  metric: RecognitionMetricKey,
): boolean {
  return effectiveMetricRecognitionIntervalMs(recognition, metric) > 0
}

export function recognitionSchedulerIntervalMs(
  recognition: CaptureProfile['recognition'],
): number {
  const demandPerMs = recognitionMetricKeys.reduce((total, metric) => {
    const interval = effectiveMetricRecognitionIntervalMs(recognition, metric)
    return interval > 0 ? total + 1 / interval : total
  }, 0)
  if (demandPerMs <= 0) return 5_000
  return Math.max(125, Math.min(5_000, Math.round(1 / demandPerMs)))
}

export function metricRecognitionRefreshIntervalMs(
  recognition: CaptureProfile['recognition'],
  metric: RecognitionMetricKey,
  baseIntervalMs: number,
  baseMaximumIntervalMs: number,
): number {
  const interval = effectiveMetricRecognitionIntervalMs(recognition, metric)
  if (interval <= 0) return Number.POSITIVE_INFINITY
  const multiplier = Math.max(1, baseMaximumIntervalMs / Math.max(1, baseIntervalMs))
  return Math.max(interval, Math.round(interval * multiplier))
}
